import type { SnakeCase } from "type-fest"

import { format } from "date-fns"
import React from "react"

import * as Geo from "@/base/geo"
import { getUserMeasurementPreferenceFromState } from "@/redux/selectors"
import { useRootSelector } from "@/redux/useRootSelector"
import i18n from "@/translations/i18n"
import * as turf from "@turf/turf"

import { logger } from "./logger"
import {
  formatPressureSensorModel, MeasurementTypes, SensorPortModes,
  SprinklerTypes,
  SwitchTypes,
} from "./sensor-configurations"
import {
  convertSensorValue,
  getSensorConversionSpec,
  requiresConversion,
} from "./sensor-conversions"
import { isNumberValid, isTruthyString } from "./type-guards"

import type {
  InstalledOrientation,
  PressureSensorModelId,
  SensorName,
} from "./sensor-configurations"
import type { MeasurementPreferenceProps } from "./sensor-conversions"
import type { AnySensorState, SensorState } from "./sensor-events"
import type { AnySensorKey } from "./sensors"
import type { LabelLength, SensorDatetimeOptions } from "./string-formatting"

export function formatSensorKey(fieldName: AnySensorKey) {
  const key = `sensorFields:${fieldName}.displayName`
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (i18n.t as any)(key)
}
/**
 * Format sensor value for user display purposes. Converts value if necessary
 * TODO: More unit tests here
 */

export function formatSensorValue({
  convertValues = true,
  dtFormat = "P pp",
  fieldName,
  measurementPreference,
  rawValue,
}: MeasurementPreferenceProps &
  SensorDatetimeOptions & {
    convertValues?: boolean
    fieldName: AnySensorKey
    rawValue: unknown
  }): string | undefined {
  const conversionUnits = getSensorConversionSpec({
    fieldName,
    measurementPreference,
    target: `user`,
  })

  if (rawValue === null || typeof rawValue === `undefined`) {
    return i18n.t("none", { ns: "common" })
  }

  // Gps Location
  if (
    typeof rawValue === `object` &&
    "coordinates" in rawValue &&
    Array.isArray(rawValue.coordinates)
  ) {
    const coordinates = rawValue.coordinates
    const [lng, lat] = coordinates.map((coord) => {
      if (isNumberValid(coord)) {
        return turf.round(coord, 2)
      }
      return null
    })
    if (typeof lng === `number` && typeof lat === `number`) {
      return `longitude=${lng}, latitude=${lat}`
    }
    return i18n.t("none")
  }
  // Booleans
  if (rawValue === true) {
    return i18n.t("true")
  }
  if (rawValue === false) {
    return i18n.t("false")
  }

  // Value should be string or number here
  const valueIsString = typeof rawValue === `string`
  const valueIsNumber = typeof rawValue === `number`
  if (!valueIsNumber && !valueIsString) {
    return i18n.t("notApplicable")
  }
  switch (fieldName) {
    case "sensorModelId": {
      return formatPressureSensorModel(rawValue as PressureSensorModelId)
    }
    case "heading": {
      if (valueIsNumber || valueIsString) {
        return i18n.t("heading.unitLabel", { ns: "sensorFields" })
      }
      return undefined
    }
    case "voltageChangeMvPerHour": {
      if (!valueIsNumber) {
        return i18n.t("none")
      }
      if (rawValue < 50 && rawValue > -50) {
        return i18n.t("notCharging")
      }
      if (rawValue < -50) {
        return i18n.t(`discharging`)
      }
      return i18n.t(`charging`)
    }
    case "reportingPeriodEndDt":
    case "reportingPeriodStartDt":
    case "statePreviousInitDt":
    case "stateCurrentInitDt": {
      try {
        return format(new Date(rawValue), dtFormat)
      } catch {
        logger.error(`Failed to format ${rawValue} with template ${dtFormat}`)

        if (valueIsString) {
          return rawValue
        }
        return `${rawValue}`
      }
    }
    case "switchType": {
      if (typeof rawValue === "string") {
        if (rawValue === SwitchTypes.CLOSED) {
          return i18n.t("sensorFields:switchType.values.C")
        } else if (rawValue === SwitchTypes.OPEN) {
          return i18n.t("sensorFields:switchType.values.O")
        }
        return i18n.t("unknown")
      }
      return undefined
    }
    case "sprinklerType": {
      if (rawValue === SprinklerTypes.BOOM) {
        return i18n.t("sprinklerType.values.boom", { ns: "sensorFields" })
      } else if (rawValue === SprinklerTypes.GUN) {
        return i18n.t("sprinklerType.values.gun", { ns: "sensorFields" })
      }
      return i18n.t("unknown")
    }
    case "applicationType": {
      if (rawValue === "water") {
        return i18n.t("applicationType.values.water", { ns: "sensorFields" })
      } else if (rawValue === "manure") {
        return i18n.t("applicationType.values.manure", { ns: "sensorFields" })
      } else if (rawValue === "other") {
        return i18n.t("applicationType.values.other", { ns: "sensorFields" })
      }
      return i18n.t("unknown")
    }
    case "speedMeasurementMethod": {
      if (rawValue === "spool_rotation") {
        return i18n.t("speedMeasurementMethod.values.spool_rotation", { ns: "sensorFields" })
      } else if (rawValue === "hose_wheel") {
        return i18n.t("speedMeasurementMethod.values.hose_wheel", { ns: "sensorFields" })
      }
      return i18n.t("unknown")
    }
    case "stateCurrent":
    case "statePrevious": {
      try {
        return i18n.t(`sensorStates:${rawValue as AnySensorState}.short`)
      } catch (error) {
        logger.error(error)
        return isTruthyString(rawValue) ? rawValue : ``
      }
    }
    case "gpsType": {
      if (valueIsString) {
        return rawValue
      }
      return undefined
    }
    case "location": {
      if (
        typeof rawValue === `object` &&
        `type` in rawValue &&
        `coordinates` in rawValue
      ) {
        const [lng, lat] = Geo.point(rawValue)?.getCoords() ?? []
        if (isNumberValid(lng) && isNumberValid(lat)) {
          return `Longitude: ${lng.toFixed(2)};\nLatitude: ${lat.toFixed(2)}`
        }
      } else {
        return i18n.t("none")
      }
      return undefined
    }
    case "installedOrientation": {
      return rawValue as InstalledOrientation
    }
    case "nSats": {
      if (valueIsNumber) {
        if (rawValue < 3) {
          return i18n.t("poor")
        }
        if (rawValue < 6) {
          return i18n.t("moderate")
        }
        return i18n.t("good")
      }
      break
    }
    case "sensorPort": {
      try {
        if (valueIsNumber) {
          if (rawValue === 1) {
            return i18n.t("sensorPort.values.port-1", { ns: "sensorFields" })
          }
          if (rawValue === 2) {
            return i18n.t("sensorPort.values.port-2", { ns: "sensorFields" })
          }
        }
      } catch (error) {
        logger.error(error)
      }
      return undefined
    }
    case "measurementType": {
      if (valueIsString) {
        if(rawValue === MeasurementTypes.ANALOG){
          return "Analog"
        }
        if(rawValue === MeasurementTypes.PULSE){
          return "Pulse"
        }
        return rawValue
      }
      return undefined
    }
    case "sensorPortMode": {
      if (valueIsString) {
        if(rawValue === SensorPortModes.SCALED_VOLTAGE){
          return "Voltage"
        }
        if(rawValue === SensorPortModes.CURRENT_LOOP){
          return "4-20ma"
        }
        return rawValue
      }
      return undefined
    }
    case "altitude":
    case "applicationRateEstimatedMm":
    case "applicationRateReportsMm":
    case "calibrationIntercept":
    case "calibrationSlope":
    case "configurationId":
    case "currentPercentage":
    case "deviceFunctionCall":
    case "diameterMm":
    case "distanceObservedMm":
    case "flowRateEstimatedLpm":
    case "hoseDiameterMm":
    case "hoseWheelMmPerMagnet":
    case "i2cAddress":
    case "internalSoc":
    case "ioPin":
    case "linearSpeedMmHMax":
    case "linearSpeedMmHMin":
    case "linearInterceptM":
    case "linearSlopeM":
    case "maxTrackingPercentage":
    case "measuredM":
    case "milliRpmAvg":
    case "milliRpmFast":
    case "milliRpmSlow":
    case "minTrackingPercentage":
    case "mlPerPulse":
    case "nMagnets":
    case "nNozzles":
    case "normalThreshold":
    case "normalThresholdLpm":
    case "flowRateOverrideLpm":
    case "nozzleDiameterMm":
    case "nWrapsOuterLayer":
    case "otaEnabled":
    case "outerHoseWrapRadiusMm":
    case "overThreshold":
    case "overThresholdLpm":
    case "pidDerivativeCoeff":
    case "pidGainCoeff":
    case "pidIntegralCoeff":
    case "pidSensorSetpoint":
    case "pidMeasurementType":
    case "pivotRadiusMeters":
    case "pressureObservedKpa":
    case "pressureReportsKpa":
    case "rampSecondsRemaining":
    case "rampSecondsTotal":
    case "rateLpmAvg":
    case "rateLpmMax":
    case "rateLpmMin":
    case "readingCelsius":
    case "readingKpa":
    case "relativePercentage":
    case "runDistanceMmCurrent":
    case "runDistanceMmElapsed":
    case "runDistanceMmMax":
    case "runMagnetCountCurrent":
    case "runMagnetCountMax":
    case "runSpeedMmpm":
    case "scheduledDeviceAction":
    case "sensorPriority":
    case "sensorTriggeredUpdate":
    case "signalQuality":
    case "signalStrength":
    case "slope":
    case "intercept":
    case "speedMillirpm":
    case "speedMmpm":
    case "speedObservedMmpm":
    case "speedReportsMmpm":
    case "startRampPercentage":
    case "swathWidthMm":
    case "targetRampPercentage":
    case "threshold":
    case "thresholdPsiLower":
    case "thresholdPsiUpper":
    case "thresholdBufferM":
    case "highThresholdM":
    case "overThresholdM":
    case "normalThresholdM":
    case "voltageIntercept":
    case "voltageMv":
    case "voltageSlope":
    case "voltageThresholdLowMv":
    case "volumeLFinal":
    case "volumeLInitial":
    case "volumeLTotal": 
    case "widthMm": {
      /*
       * DEFAULT FORMATTING
       */
      const sigDigits = conversionUnits?.sigDigits ?? 0

      // Coerce to number if possible
      let valueAsNumber: number | undefined
      if (valueIsString) {
        valueAsNumber = Number.parseFloat(rawValue)
      } else if (valueIsNumber) {
        valueAsNumber = rawValue
      }
      // Convert number with correct sig digits
      if (isNumberValid(valueAsNumber)) {
        
        if (convertValues && requiresConversion(fieldName)) {
          valueAsNumber = convertSensorValue({
            fieldName,
            measurementPreference,
            rawValue: valueAsNumber,
            target: "user",
          })
          
        }
        if (
          fieldName === 'volumeLFinal' 
          || fieldName === 'volumeLInitial' 
          || fieldName === 'volumeLTotal'
        ) {
          return new Intl.NumberFormat('en-US', {
            maximumFractionDigits: 0, // No decimals
          }).format(valueAsNumber)
        }
        // Format number as string
        if (sigDigits > 0) {
          return valueAsNumber.toFixed(sigDigits)
        }
        return `${Math.round(valueAsNumber)}`
      }
    }
  }
  return undefined
}

export function useFormatSensorValue() {
  const measurementPreference = useRootSelector(
    getUserMeasurementPreferenceFromState,
  )

  return React.useCallback(
    (fieldName: AnySensorKey, rawValue: unknown) => {
      return formatSensorValue({
        fieldName,
        measurementPreference,
        rawValue,
      })
    },
    [measurementPreference],
  )
}
export function formatSensorName(
  sensorName: SensorName | SnakeCase<SensorName> | string | undefined,
): string {
  if (sensorName) {
    switch (sensorName) {
      case "battery": {
        return i18n.t("sensors:battery")
      }
      case "depth": {
        return i18n.t("sensors:depth")
      }
      case "flow": {
        return i18n.t("sensors:flow")
      }
      case "gps": {
        return i18n.t("sensors:gps")
      }
      case "hall_switch":
      case "hallSwitch": {
        return i18n.t("sensors:hallSwitch")
      }
      case "humidity": {
        return i18n.t("sensors:humidity")
      }
      case "icm": {
        return i18n.t("sensors:icm")
      }

      case "pressure": {
        return i18n.t("sensors:pressure")
      }
      case "pressure_switch":
      case "pressureSwitch": {
        return i18n.t("sensors:pressureSwitch")
      }
      case "reel": {
        return i18n.t("sensors:reel")
      }
      case "relay": {
        return i18n.t("sensors:relay")
      }
      case "temperature": {
        return i18n.t("sensors:temperature")
      }
      case "vfd": {
        return i18n.t("sensors:vfd")
      }
      case "wheel": {
        return i18n.t("sensors:wheel")
      }
      case "device": {
        return i18n.t("sensors:device")
      }
      case "ioExpander":
      case "io_expander": {
        return i18n.t("sensors:ioExpander")
      }
      case "pivotControl":
      case "center_pivot_controller": {
        return i18n.t("sensors:pivotControl")
      }
    }
  }
  return ""
}

export function formatSensorState(
  sensorState: SensorState,
  options?: { labelLength?: LabelLength },
) {
  const labelLength = options?.labelLength ?? "short"
  return i18n.t(`sensorStates:${sensorState}.${labelLength}`)
}
export function useFormatSensorState() {
  return formatSensorState
}
